import { LoadingStatus } from './LoadingStatus';
import { UseState } from '../util/TypeUtil';

export type LoadingProps = UseState<LoadingState>;

export interface LoadingState {
  status: LoadingStatus;
  errorMessage?: string;
  warningMessage?: string;
  disableLoadingIndicator?: boolean;
}

const activeTasksMap = new WeakMap<object, Set<string>>();

/**
 * Utility method to allow calling component to automatically set loading states while running setup functionality.
 * Caller should return LoadState in the UseEffect to allow React to update `isCancelled` property once the
 * component is unmounted. This allows states updates to not run if the component is no longer mounted.
 *
 * Each component maintains its own list of active tasks, with the loading state updating based on
 * whether the list is empty (Complete) or has remaining tasks (Loading).
 *
 * LoadingIndicator.tsx is best coupled with this functionality to allow for easy loading indicators and error indicators
 * @param setLoadingState
 * @param func
 * @constructor
 */
export function LoadState(
  setLoadingState: (value: ((prevState: LoadingState) => LoadingState) | LoadingState) => void = () => {},
  func: () => Promise<void>
) {
  let isCancelled = false;
  const taskKey = generateKey();

  const componentInstance = setLoadingState;
  let activeTasks = activeTasksMap.get(componentInstance);
  if (!activeTasks) {
    activeTasks = new Set<string>();
    activeTasksMap.set(componentInstance, activeTasks);
  }

  activeTasks.add(taskKey);
  setLoadingState(current => ({ ...current, status: 'Loading' as LoadingStatus }));

  func()
    .then(() => {
      if (isCancelled || !setLoadingState) return;

      activeTasks!.delete(taskKey);
      setLoadingState(current => ({
        ...current,
        status: activeTasks!.size === 0 ? ('Complete' as LoadingStatus) : ('Loading' as LoadingStatus),
      }));
    })
    .catch((e: Error) => {
      if (isCancelled || !setLoadingState) return;

      activeTasks!.delete(taskKey);
      setLoadingState(current => ({
        ...current,
        status: 'Error' as LoadingStatus,
        errorMessage: getErrorMessage(e),
      }));
    });

  return () => {
    isCancelled = true;
    activeTasks!.delete(taskKey);
    setLoadingState(current => ({
      ...current,
      status: activeTasks!.size === 0 ? ('Complete' as LoadingStatus) : ('Loading' as LoadingStatus),
    }));
  };
}

export function ErrorManagement(
  initialState: LoadingStatus,
  setLoadingState: (value: ((prevState: LoadingState) => LoadingState) | LoadingState) => void,
  func: () => Promise<void>
) {
  if (initialState) {
    setLoadingState({ status: initialState });
  }

  func().catch((e: any) => {
    if (!setLoadingState) {
      return;
    }

    setLoadingState({ status: 'Error', errorMessage: getErrorMessage(e) });
  });
}

export async function ErrorManagementAsync<T>(
  initialState: LoadingStatus,
  setLoadingState: (value: ((prevState: LoadingState) => LoadingState) | LoadingState) => void,
  func: () => Promise<T>
): Promise<T | undefined> {
  if (initialState) {
    setLoadingState({ status: initialState });
  }

  try {
    return await func();
  } catch (e) {
    if (!setLoadingState) {
      return undefined;
    }

    setLoadingState({ status: 'Error', errorMessage: getErrorMessage(e) });
  }

  return undefined;
}

function getErrorMessage(e: any): string {
  let message;
  if (e instanceof Error) {
    message = e.message;

    try {
      const parsed = JSON.parse(message);
      const subMessage = parsed?.message;
      message = subMessage ?? message;
    } catch (jsonParseError) {
      message = e.message;
    }
  } else {
    message = String(e);
  }

  return message ?? 'Unknown Error';
}

function generateKey() {
  return (Math.random() + 1).toString(36).substring(7);
}
